import { sortBy } from 'lodash';

import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';
import { ensureString } from 'utils/string';

export const getIndustryPartnerCRMRecords = ({
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/records/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIPDBList = ({
  params = {},
  version = 'v1',
  enableSponsorGrouping = false,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/ipdb-list/`,
      method: RequestMethod.GET,
      params,
    })
    .then(({ data }) => {
      if (!enableSponsorGrouping) {
        return { data };
      }
      const searchStringLowerCase = ensureString(params?.search).toLowerCase();
      const cloneData = { ...data };
      const res = data.results
        .reduce((arr, obj) => {
          if (obj.isAccountClaimed && !obj.crmAffiliationId) {
            arr.push({
              ...obj,
              organizationName: obj.affiliationOrganizationName,
              type: 'Member added',
            });
          } else {
            arr.push({
              ...obj,
              organizationName: obj.affiliationOrganizationName,
              type: 'District added',
            });
          }
          return arr;
        }, [])
        .filter(
          rec =>
            ensureString(rec.userFullName)
              .toLowerCase()
              .includes(searchStringLowerCase) ||
            ensureString(rec.email)
              .toLowerCase()
              .includes(searchStringLowerCase) ||
            ensureString(rec.affiliationOrganizationName)
              .toLowerCase()
              .includes(searchStringLowerCase),
        );
      // Need to sort by type first in order for groupBy logic to work properly
      cloneData.results = sortBy(res, 'type');
      return { data: cloneData };
    })
    .catch(handleError);

export const getIPDBListCount = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/ipdb-list/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportIndustryPartnerCRMRecords = ({
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/crm-csv-report/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const exportIndustryPartnerCRMOrganizations = ({
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/crm-organizations-csv-report/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRecord = ({ id, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/records/${id}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createRecord = ({ payload, params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/records/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateRecord = ({ id, payload, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/records/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteRecord = ({ id, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/records/${id}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRecordEventLogs = ({ recordId, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/records/${recordId}/events/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRecordPlacementLogs = ({
  recordId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/records/${recordId}/placements/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRecordAffiliations = ({ id, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/records/${id}/affiliations/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createMemberInvite = ({ id, payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/records/${id}/invite`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFields = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/fields/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFieldOptions = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/field-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrganization = ({ params = {}, version = 'v1', id } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/organizations/${id}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrganizations = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/organizations/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createAffiliation = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/affiliations/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateAffiliation = ({ id, payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/affiliations/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createOrganization = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/organizations/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateOrganization = ({
  id,
  payload,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/organizations/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createField = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/fields/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateField = ({ id, payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/fields/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteField = ({ id, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/fields/${id}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createFieldOption = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/field-options/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateFieldOption = ({ id, payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/field-options/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteFieldOption = ({ id, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/field-options/${id}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEntityFieldOption = ({ payload, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/entity-field-options/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEntityFieldOption = ({ id, version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/entity-field-options/${id}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCRMInvites = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/ip-crm/invites/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCRMInviteCounts = ({ version = 'v1' }) =>
  client
    .request({
      url: `${version}/ip-crm/invites/counts/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const connectToDistrict = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/ip-crm/invites/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const approveCRMInvite = ({ version = 'v1', id }) =>
  client
    .request({
      url: `${version}/ip-crm/invites/${id}/accept/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const declineCRMInvite = ({ version = 'v1', id }) =>
  client
    .request({
      url: `${version}/ip-crm/invites/${id}/decline/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const undoCRMInvite = ({ version = 'v1', id, payload }) =>
  client
    .request({
      url: `${version}/ip-crm/invites/${id}/undo/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createFreeResponse = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/ip-crm/free-responses/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateFreeResponse = ({ version = 'v1', id, payload }) =>
  client
    .request({
      url: `${version}/ip-crm/free-responses/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const freeResponseRecord = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/ip-crm/free-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createURLResponse = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/ip-crm/url-responses/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateURLResponse = ({ version = 'v1', id, payload }) =>
  client
    .request({
      url: `${version}/ip-crm/url-responses/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getURLResponse = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/ip-crm/url-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createDateResponse = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/ip-crm/date-responses/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateDateResponse = ({ version = 'v1', id, payload }) =>
  client
    .request({
      url: `${version}/ip-crm/date-responses/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDateResponse = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/ip-crm/date-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getMemberCrmRecords = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/ip-crm/member-crm-record/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCityStateList = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/ip-crm/records/city_state_list/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createSponsorPlaceholderUser = ({ version = 'v1', crmRecordId }) =>
  client
    .request({
      url: `${version}/ip-crm/records/${crmRecordId}/placeholder-user/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getRecordCreatedByOptions = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/ip-crm/records/created-by-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getOrganizationCreatedByOptions = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/ip-crm/ipdb-organizations/created-by-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEngagementLogCreatedByOptions = ({ version = 'v1', params }) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-logs/created-by-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEngagementLogs = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-logs/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEngagementLogsCount = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-logs/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEngagementLog = ({ version = 'v1', payload, params } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-logs/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateEngagementLog = ({
  version = 'v1',
  payload,
  engagementLogId,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-logs/${engagementLogId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEngagementLog = ({ version = 'v1', engagementLogId } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-logs/${engagementLogId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEngagementLogFields = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-fields/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEngagementLogField = ({
  version = 'v1',
  payload,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-fields/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateEngagementLogField = ({
  fieldId,
  payload,
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-fields/${fieldId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEngagementLogField = ({ fieldId, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-fields/${fieldId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEngagementLogFieldOptions = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-field-options/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEngagementLogFieldOptions = ({
  version = 'v1',
  payload,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-field-options/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateEngagementLogFieldOptions = ({
  fieldOptionId,
  payload,
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-field-options/${fieldOptionId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEngagementLogFieldOptions = ({
  fieldOptionId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-field-options/${fieldOptionId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEngagementLogFreeResponses = ({
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-free-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEngagementLogFreeResponse = ({
  version = 'v1',
  payload,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-free-responses/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateEngagementLogFreeResponse = ({
  freeResponseId,
  payload,
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-free-responses/${freeResponseId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEngagementLogFreeResponse = ({
  freeResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-free-responses/${freeResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEngagementLogURLResponses = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-url-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEngagementLogURLResponse = ({
  version = 'v1',
  payload,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-url-responses/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateEngagementLogURLResponse = ({
  urlResponseId,
  payload,
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-url-responses/${urlResponseId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEngagementLogURLResponse = ({
  urlResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-url-responses/${urlResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEngagementLogDateResponses = ({
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-date-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEngagementLogDateResponse = ({
  version = 'v1',
  payload,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-date-responses/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateEngagementLogDateResponse = ({
  dateResponseId,
  payload,
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-date-responses/${dateResponseId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEngagementLogDateResponse = ({
  dateResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-date-responses/${dateResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEngagementLogFileResponses = ({
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-file-upload-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEngagementLogFileResponse = ({
  version = 'v1',
  payload,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-file-upload-responses/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateEngagementLogFileResponse = ({
  fileResponseId,
  payload,
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-file-upload-responses/${fileResponseId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEngagementLogFileResponse = ({
  fileResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-file-upload-responses/${fileResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getEngagementLogRecordResponses = ({
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-record-responses/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEngagementLogRecordResponse = ({
  version = 'v1',
  payload,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-record-responses/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateEngagementLogRecordResponse = ({
  recordResponseId,
  payload,
  version = 'v1',
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-record-responses/${recordResponseId}/`,
      method: RequestMethod.PATCH,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteEngagementLogRecordResponse = ({
  recordResponseId,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-record-responses/${recordResponseId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getPartnershipCategories = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-categories/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createEngagementLogCategory = ({
  version = 'v1',
  payload,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/engagement-log-categories/`,
      method: RequestMethod.POST,
      data: payload,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIPDBOrganizationList = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/ipdb-organizations/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIPDBOrganizationListCount = ({
  params = {},
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/ipdb-organizations/count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIPDBOrganization = ({ id, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/ip-crm/ipdb-organizations/${id}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIPDBOrganizationWithHashId = ({
  hashId,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/ipdb-organizations/retrieve-with-hash-id/${hashId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getIPDBIntakeSetting = ({
  organizationUuid,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/ip-crm/intake-setting/${organizationUuid}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
